import { Colors, Lightning } from '@lightningjs/sdk';
import { getImageTextureObj } from 'support/generalUtils';
import { LoginRadiusError } from 'types/loginRadius';
import { translate } from 'support/translate';
import AbstractToast from './AbstractToast';
import TextLabel from '../TextLabel';

const MIN_HEIGHT = 72; // LABEL_LINE_HEIGHT + PADDING_VERTICAL * 2
const DEFAULT_RADIUS = 8;
const DEFAULT_BORDER_WIDTH = 2;
const LABEL_LINE_HEIGHT = 32;
const PADDING_LEFT = 24;
const PADDING_VERTICAL = 8; // Vertical padding top and bottom

const ICON_CIRCLE_ALERT = getImageTextureObj(
  'static/images/circle-alert.svg',
  36,
  36,
);

export interface ServiceErrorToastTemplateSpec
  extends Lightning.Component.TemplateSpec {
  StartIcon: Lightning.textures.ImageTexture;
  Label: typeof TextLabel;
}

export default class ServiceErrorToast extends AbstractToast {
  updateLabel(error: LoginRadiusError) {
    // Enhance error message with field-specific details if available
    let message = error.Message ?? '';
    if (error.Errors && error.Errors.length > 0) {
      message = error.Errors.map(err => err.ErrorMessage).join(', ');
    }

    const errorLabel = translate(
      'error.toast.service',
      message,
      error.ErrorCode,
    );

    // Reset label height to force recalculation
    this._Label.patch({ h: undefined });

    // Update text content
    this._Label.patch({ text: { text: errorLabel } });

    // Wait for text to render, then check actual height
    requestAnimationFrame(() => {
      const messuredLabelHeight = this._Label.finalH;
      const newHeight = Math.max(
        MIN_HEIGHT,
        messuredLabelHeight + PADDING_VERTICAL * 2,
      );
      this.patch({ h: newHeight });
    });
  }

  override _setup() {
    this.patch({
      h: MIN_HEIGHT,
      color: Colors('serviceErrorBackground').get(),
      flex: {
        justifyContent: 'flex-start',
        paddingTop: PADDING_VERTICAL,
        paddingBottom: PADDING_VERTICAL,
      },
      shader: {
        type: Lightning.shaders.RoundedRectangle,
        radius: DEFAULT_RADIUS,
        stroke: DEFAULT_BORDER_WIDTH,
        strokeColor: Colors('serviceErrorBorder').get(),
      },
      Image: {
        ...ICON_CIRCLE_ALERT,
        shader: null,
      },
      Label: {
        type: TextLabel,
        text: {
          verticalAlign: 'middle',
          textColor: Colors('labelText').get(),
          maxLines: 3,
          lineHeight: LABEL_LINE_HEIGHT,
        },
        flexItem: {
          marginLeft: PADDING_LEFT,
        },
        shader: null,
      },
    });
  }
}
